import React, { useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import VehicleMarkers from './VehicleMarkers';

const containerStyle = {
  width: '100%',
  height: '100vh'
};

const defaultCenter = { lat: -23.7165, lng: -46.8496 };

function MapGoogle({
  onMarkerClick,
  routePath,
  centerMapPosition,
  vehicles = [],
  focusVehicle,
  userLocation,
  nearbyStops = [],
  tripOverlay,
  tripOverlayLoading,
  tripOverlayError,
  onVehicleClick,
  // Novo: filtros opcionais para controlar quais veículos devem ser exibidos
  visibleRouteShortNames = [],
  visibleVehicleId = null,
  // Novo: contador para forçar recentralização em cliques
  focusRequestId = 0,
}) {
  const mapRef = useRef(null);
  const vehicleMarkersRef = useRef(null);
  const lastOpenedVehicleIdRef = useRef(null);
  const lastFocusRequestIdRef = useRef(0);

  const userLocationMarkerRef = useRef(null);
  const nearbyStopMarkersRef = useRef([]);

  const tripPolylineCasingRef = useRef(null);
  const tripPolylineRef = useRef(null);
  const tripStopMarkersRef = useRef([]);
  const tripStopsInfoWindowRef = useRef(null);
  const lastTripIdRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Auto-fit bounds when route changes
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !routePath || routePath.length === 0) return;
    
    const allPoints = [];
    
    // Check if routePath is array of segments with points
    if (Array.isArray(routePath[0]?.points)) {
      routePath.forEach(seg => {
        if (seg.points) {
          seg.points.forEach(p => allPoints.push({ lat: p[0], lng: p[1] }));
        }
      });
    } else if (Array.isArray(routePath) && Array.isArray(routePath[0])) {
      // routePath is a flat array of [lat, lng]
      routePath.forEach(p => allPoints.push({ lat: p[0], lng: p[1] }));
    }
    
    if (allPoints.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      allPoints.forEach(point => bounds.extend(point));
      mapRef.current.fitBounds(bounds, { padding: 20 });
    }
  }, [routePath, isLoaded]);

  // Imperative overlays (polylines and markers) management to avoid stale overlays
  const polylinesRef = useRef([]);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Clear existing polylines and markers
    polylinesRef.current.forEach(p => p.setMap(null));
    polylinesRef.current = [];
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (!routePath || routePath.length === 0) return;

    const allSegmentPaths = [];
    // Build paths
    if (Array.isArray(routePath[0]?.points)) {
      routePath.forEach(seg => {
        if (seg.points && seg.points.length) {
          const path = seg.points.map(p => ({ lat: p[0], lng: p[1] }));
          allSegmentPaths.push({ path, mode: seg.mode, routeColor: seg.routeColor });
        }
      });
    } else if (Array.isArray(routePath) && Array.isArray(routePath[0])) {
      const flat = routePath.map(p => ({ lat: p[0], lng: p[1] }));
      allSegmentPaths.push({ path: flat, mode: 'FLAT' });
    }

    const normalizeColor = (c) => {
      if (!c) return null;
      if (typeof c !== 'string') return null;
      if (c.startsWith('#')) return c;
      return `#${c}`;
    };

    // Create polylines (Google Maps-like: casing + rounded caps)
    allSegmentPaths.forEach((seg) => {
      const isWalk = seg.mode === 'WALK';
      const baseColor = isWalk
        ? '#6b7280'
        : (normalizeColor(seg.routeColor) || '#4CAF50');

      const innerWeight = isWalk ? 4 : 6;
      const casingWeight = innerWeight + 4;
      const zIndex = isWalk ? 10 : 20;

      const common = {
        path: seg.path,
        geodesic: true,
        clickable: false,
        strokeLineCap: 'round',
        strokeLineJoin: 'round',
        zIndex,
      };

      // White casing underlay
      const casing = new window.google.maps.Polyline({
        ...common,
        strokeColor: '#ffffff',
        strokeOpacity: 0.9,
        strokeWeight: casingWeight,
      });
      casing.setMap(mapRef.current);
      polylinesRef.current.push(casing);

      if (isWalk && window.google) {
        // Dashed walk line (use symbols)
        const dashed = new window.google.maps.Polyline({
          ...common,
          strokeOpacity: 0,
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                strokeWeight: 2,
                scale: 4,
              },
              offset: '0',
              repeat: '12px',
            },
          ],
        });
        dashed.setMap(mapRef.current);
        polylinesRef.current.push(dashed);
        return;
      }

      // Solid transit line overlay
      const line = new window.google.maps.Polyline({
        ...common,
        strokeColor: baseColor,
        strokeOpacity: 0.95,
        strokeWeight: innerWeight,
      });
      line.setMap(mapRef.current);
      polylinesRef.current.push(line);
    });

    // Start / End markers
    let startPos = null;
    let endPos = null;
    if (allSegmentPaths.length > 0) {
      const first = allSegmentPaths[0];
      const last = allSegmentPaths[allSegmentPaths.length - 1];
      if (first.path && first.path.length) startPos = first.path[0];
      if (last.path && last.path.length) endPos = last.path[last.path.length - 1];
    }

    const makeSvgDataUrl = (letter, color) => {
      const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'><circle cx='12' cy='12' r='10' fill='${color}'/><text x='12' y='16' text-anchor='middle' fill='white' font-size='12' font-weight='bold'>${letter}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    };

    if (startPos) {
      const marker = new window.google.maps.Marker({
        position: startPos,
        map: mapRef.current,
        icon: { url: makeSvgDataUrl('A', 'green'), scaledSize: new window.google.maps.Size(24, 24) }
      });
      markersRef.current.push(marker);
    }
    if (endPos) {
      const marker = new window.google.maps.Marker({
        position: endPos,
        map: mapRef.current,
        icon: { url: makeSvgDataUrl('B', 'red'), scaledSize: new window.google.maps.Size(24, 24) }
      });
      markersRef.current.push(marker);
    }

    return () => {
      polylinesRef.current.forEach(p => p.setMap(null));
      polylinesRef.current = [];
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    };
  }, [routePath, isLoaded]);

  // Center on a position when requested
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (centerMapPosition && centerMapPosition.lat && centerMapPosition.lon) {
      const { lat, lon } = centerMapPosition;
      mapRef.current.panTo({ lat, lng: lon });
      mapRef.current.setZoom(17);
    }
  }, [centerMapPosition, isLoaded]);

  // Renderizar paradas próximas + localização do usuário
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    // Localização do usuário
    if (userLocation?.lat && userLocation?.lng) {
      const pos = { lat: userLocation.lat, lng: userLocation.lng };
      if (!userLocationMarkerRef.current) {
        userLocationMarkerRef.current = new window.google.maps.Marker({
          map: mapRef.current,
          position: pos,
          title: 'Minha localização',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: '#6366f1',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          zIndex: 9999,
        });
      } else {
        userLocationMarkerRef.current.setPosition(pos);
        userLocationMarkerRef.current.setMap(mapRef.current);
      }
    } else if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.setMap(null);
    }

    // Paradas próximas
    nearbyStopMarkersRef.current.forEach((m) => m.setMap(null));
    nearbyStopMarkersRef.current = [];

    if (Array.isArray(nearbyStops) && nearbyStops.length > 0) {
      nearbyStopMarkersRef.current = nearbyStops
        .filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number')
        .map((stop) => {
          return new window.google.maps.Marker({
            map: mapRef.current,
            position: { lat: stop.lat, lng: stop.lng },
            title: stop.stopName || 'Parada',
          });
        });
    }
  }, [isLoaded, userLocation, nearbyStops]);

  // Desenhar percurso completo (shape) + paradas do trip selecionado
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    // Sempre limpar overlays anteriores antes de desenhar novos
    if (tripPolylineRef.current) {
      tripPolylineRef.current.setMap(null);
      tripPolylineRef.current = null;
    }
    if (tripPolylineCasingRef.current) {
      tripPolylineCasingRef.current.setMap(null);
      tripPolylineCasingRef.current = null;
    }
    tripStopMarkersRef.current.forEach((m) => m.setMap(null));
    tripStopMarkersRef.current = [];

    if (!tripOverlay || !tripOverlay.shape || !Array.isArray(tripOverlay.shape.coordinates)) {
      lastTripIdRef.current = null;
      return;
    }

    // GeoJSON: [[lng, lat], ...] -> Google Maps: {lat, lng}
    const path = tripOverlay.shape.coordinates
      .filter((c) => Array.isArray(c) && c.length >= 2)
      .map(([lng, lat]) => ({ lat, lng }));

    if (path.length < 2) {
      lastTripIdRef.current = null;
      return;
    }

    const routeColorRaw = tripOverlay.routeColor;
    const routeColor =
      typeof routeColorRaw === 'string'
        ? (routeColorRaw.startsWith('#') ? routeColorRaw : `#${routeColorRaw}`)
        : '#111827';

    // Casing (borda branca)
    tripPolylineCasingRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      clickable: false,
      strokeColor: '#ffffff',
      strokeOpacity: 0.95,
      strokeWeight: 10,
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      zIndex: 30,
    });
    tripPolylineCasingRef.current.setMap(mapRef.current);

    // Linha principal
    tripPolylineRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      clickable: false,
      strokeColor: routeColor,
      strokeOpacity: 0.95,
      strokeWeight: 6,
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      zIndex: 31,
    });
    tripPolylineRef.current.setMap(mapRef.current);

    // Fit bounds só quando trocar de trip (evita ficar brigando com o usuário)
    const tripId = tripOverlay.tripId || null;
    const shouldFit = tripId && lastTripIdRef.current !== tripId;
    lastTripIdRef.current = tripId;
    if (shouldFit) {
      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds, { padding: 40 });
    }

    // Paradas
    const stops = Array.isArray(tripOverlay.stops) ? tripOverlay.stops : [];
    if (!tripStopsInfoWindowRef.current) {
      tripStopsInfoWindowRef.current = new window.google.maps.InfoWindow();
    }

    tripStopMarkersRef.current = stops
      .filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number')
      .map((stop) => {
        const marker = new window.google.maps.Marker({
          map: mapRef.current,
          position: { lat: stop.lat, lng: stop.lng },
          title: stop.stopName || 'Parada',
          zIndex: 2000,
        });

        marker.addListener('click', () => {
          const content = `
            <div style="min-width:220px; padding:8px;">
              <div style="font-weight:700; margin-bottom:6px;">${stop.stopName || 'Parada'}</div>
              <div style="font-size:12px; color:#374151;">Seq: ${stop.sequence ?? '-'}</div>
              <div style="font-size:12px; color:#6b7280; margin-top:6px;">
                ${stop.arrivalTime ? `Chegada: ${stop.arrivalTime}` : ''}
                ${stop.arrivalTime && stop.departureTime ? '<br/>' : ''}
                ${stop.departureTime ? `Saída: ${stop.departureTime}` : ''}
              </div>
            </div>
          `;
          tripStopsInfoWindowRef.current.setContent(content);
          tripStopsInfoWindowRef.current.open({ map: mapRef.current, anchor: marker });
        });

        return marker;
      });
  }, [tripOverlay, isLoaded]);

  // Cleanup do overlay ao desmontar
  useEffect(() => {
    return () => {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.setMap(null);
        userLocationMarkerRef.current = null;
      }

      nearbyStopMarkersRef.current.forEach((m) => m.setMap(null));
      nearbyStopMarkersRef.current = [];

      if (tripPolylineRef.current) {
        tripPolylineRef.current.setMap(null);
        tripPolylineRef.current = null;
      }
      if (tripPolylineCasingRef.current) {
        tripPolylineCasingRef.current.setMap(null);
        tripPolylineCasingRef.current = null;
      }
      tripStopMarkersRef.current.forEach((m) => m.setMap(null));
      tripStopMarkersRef.current = [];
      if (tripStopsInfoWindowRef.current) {
        tripStopsInfoWindowRef.current.close();
        tripStopsInfoWindowRef.current = null;
      }
    };
  }, []);

  // Focar em um veículo específico quando solicitado
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    if (!focusVehicle) return;

    // Só recentraliza quando o usuário faz um novo clique (focusRequestId muda)
    if (typeof focusRequestId === 'number' && focusRequestId > 0) {
      if (lastFocusRequestIdRef.current === focusRequestId) return;
      lastFocusRequestIdRef.current = focusRequestId;
    }

    const lat = Number(focusVehicle?.lat);
    const lng = Number(focusVehicle?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const position = { lat, lng };
    mapRef.current.panTo(position);
    mapRef.current.setZoom(17);

    // Abrir InfoWindow do veículo (através do VehicleMarkers)
    /*
    // Temporariamente desabilitado: não abrir InfoWindow automaticamente
    if (vehicleMarkersRef.current.openInfoWindow) {
      vehicleMarkersRef.current.openInfoWindow(vehicle.vehicleId);
    }
    */
  }, [focusVehicle, isLoaded]);


  if (loadError) return <div>Erro ao carregar Google Maps</div>;
  if (!isLoaded) return <div>Carregando mapa...</div>;

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    rotateControl: false,
    scaleControl: false,
    clickableIcons: false,
    gestureHandling: 'greedy',
  };

  

  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={15}
        options={mapOptions}
        onLoad={(map) => { mapRef.current = map; }}
      />
      {/* Renderizar marcadores de veículos (filtrados quando solicitado) */}
      {isLoaded && mapRef.current && (
        <VehicleMarkers
          ref={vehicleMarkersRef}
          vehicles={
            // Se um veículo específico foi solicitado, mostrar só ele.
            visibleVehicleId
              ? vehicles.filter((v) => v.vehicleId === visibleVehicleId)
              : // Senão, se houver uma lista de linhas para rastrear, filtrar por routeShortName
              (Array.isArray(visibleRouteShortNames) && visibleRouteShortNames.length > 0
                ? vehicles.filter((v) => visibleRouteShortNames.includes(v.routeShortName))
                : vehicles)
          }
          map={mapRef.current}
          onVehicleClick={onVehicleClick}
        />
      )}
    </>
  );
}

export default React.memo(MapGoogle);
